import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, DollarSign, ChevronDown, ChevronUp, Search, Share } from 'lucide-react';
import { Encounter } from '../../types/encounter';
import { useAuth } from '../../contexts/AuthContext';
import {
  getCatalogItemsByCategory,
  getEncounterOrders,
  addOrderLineItem,
  updateOrderLineItem,
  deleteOrderLineItem
} from '../../utils/billingApi';
import {
  CatalogItem,
  OrderLineItem,
  BillableItemCategory
} from '../../types/billing';
import ConfirmDialog from '../common/ConfirmDialog';
import { CardData } from '../../utils/chatApi';
import { useChatDock } from '../../contexts/ChatDockContext';

interface OrdersSectionProps {
  encounter: Encounter | null;
  onUpdate: () => void;
  petId: string;
  petName: string;
  petPhotoUrl: string | null;
}

interface CategorySection {
  category: BillableItemCategory;
  label: string;
  items: CatalogItem[];
  loading: boolean;
  expanded: boolean;
}

const OrdersSection: React.FC<OrdersSectionProps> = ({ encounter, onUpdate, petId, petName, petPhotoUrl }) => {
  const { user } = useAuth();
  const { composeShare } = useChatDock();
  const [categories, setCategories] = useState<CategorySection[]>([
    { category: 'Diagnostic', label: 'Diagnostics', items: [], loading: false, expanded: false },
    { category: 'Treatment', label: 'Treatments', items: [], loading: false, expanded: false },
    { category: 'Medication', label: 'Medications', items: [], loading: false, expanded: false },
    { category: 'Product', label: 'Other Products', items: [], loading: false, expanded: false },
    { category: 'Labs', label: 'Labs', items: [], loading: false, expanded: false },
    { category: 'Immunizations', label: 'Immunizations', items: [], loading: false, expanded: false },
  ]);

  const [orderItems, setOrderItems] = useState<OrderLineItem[]>([]);
  const [totals, setTotals] = useState({ subtotal: 0, total_discount: 0, grand_total: 0, item_count: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerms, setSearchTerms] = useState<Record<BillableItemCategory, string>>({
    'Diagnostic': '',
    'Treatment': '',
    'Medication': '',
    'Product': '',
    'Labs': '',
    'Immunizations': ''
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<OrderLineItem | null>(null);

  // Local editing state for number fields (allows empty strings while typing)
  const [editingValues, setEditingValues] = useState<Record<string, {
    quantity?: string;
    price?: string;
    discount?: string;
  }>>({});

  // Debounce timers
  const debounceTimers = useRef<Record<string, NodeJS.Timeout>>({});

  useEffect(() => {
    if (encounter && user?.practice_id) {
      fetchOrders();
    }
  }, [encounter?.id, user?.practice_id]);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach(timer => clearTimeout(timer));
    };
  }, []);

  const fetchOrders = async () => {
    if (!encounter) return;

    setLoading(true);
    try {
      const summary = await getEncounterOrders(encounter.id);
      setOrderItems(summary.line_items);
      setTotals({
        subtotal: summary.subtotal,
        total_discount: summary.total_discount,
        grand_total: summary.grand_total,
        item_count: summary.item_count
      });
    } catch (err: any) {
      console.error('Failed to fetch orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCategoryItems = async (categoryIndex: number) => {
    if (!user?.practice_id) return;

    const category = categories[categoryIndex];
    if (category.items.length > 0) return; // Already loaded

    // Set loading state
    setCategories(prev => prev.map((cat, idx) =>
      idx === categoryIndex ? { ...cat, loading: true } : cat
    ));

    try {
      const items = await getCatalogItemsByCategory(user.practice_id, category.category);
      setCategories(prev => prev.map((cat, idx) =>
        idx === categoryIndex ? { ...cat, items, loading: false } : cat
      ));
    } catch (err: any) {
      console.error('Failed to load catalog items:', err);
      setCategories(prev => prev.map((cat, idx) =>
        idx === categoryIndex ? { ...cat, loading: false } : cat
      ));
    }
  };

  const toggleCategory = async (categoryIndex: number) => {
    const category = categories[categoryIndex];

    if (!category.expanded) {
      await loadCategoryItems(categoryIndex);
    }

    setCategories(prev => prev.map((cat, idx) =>
      idx === categoryIndex ? { ...cat, expanded: !cat.expanded } : cat
    ));
  };

  const [unscheduledItem, setUnscheduledItem] = useState<CatalogItem | null>(null);
  const [showUnscheduledConfirm, setShowUnscheduledConfirm] = useState(false);
  const [unscheduledWarningMsg, setUnscheduledWarningMsg] = useState('');

  const handleAddItem = async (catalogItem: CatalogItem, confirmUnscheduled = false) => {
    if (!encounter) return;

    try {
      await addOrderLineItem(encounter.id, {
        catalog_item_id: catalogItem.id,
        quantity: 1,
        discount_amount: 0,
        confirm_unscheduled: confirmUnscheduled
      });
      await fetchOrders();
      onUpdate();
      // Reset if successful
      if (confirmUnscheduled) {
        setUnscheduledItem(null);
        setShowUnscheduledConfirm(false);
      }
    } catch (err: any) {
      console.error('Failed to add item:', err);

      // Check for 409 Conflict (Unscheduled Vaccine Warning)
      if (err.status === 409 || (err.message && err.message.includes('not on this pet\'s schedule'))) {
        setUnscheduledItem(catalogItem);
        setUnscheduledWarningMsg(err.message || "This vaccine is not on the schedule.");
        setShowUnscheduledConfirm(true);
      } else {
        alert('Failed to add item: ' + err.message);
      }
    }
  };

  const confirmUnscheduledAdd = () => {
    if (unscheduledItem) {
      handleAddItem(unscheduledItem, true);
    }
  };

  // Debounced update function
  const debouncedUpdate = (
    itemId: string,
    field: 'quantity' | 'unit_price' | 'discount_amount',
    value: number,
    lineItem: OrderLineItem
  ) => {
    const timerId = `${itemId}-${field}`;

    // Clear existing timer
    if (debounceTimers.current[timerId]) {
      clearTimeout(debounceTimers.current[timerId]);
    }

    // Set new timer
    debounceTimers.current[timerId] = setTimeout(async () => {
      if (!encounter) return;

      // Validate values
      if (field === 'quantity' && value <= 0) return;
      if (field === 'unit_price' && value <= 0) return;
      if (field === 'discount_amount' && value < 0) return;

      try {
        await updateOrderLineItem(encounter.id, lineItem.id, {
          [field]: value
        });
        await fetchOrders();
        onUpdate();

        // Clear editing state after successful update
        setEditingValues(prev => {
          const updated = { ...prev };
          if (updated[itemId]) {
            const fieldMap = { quantity: 'quantity', unit_price: 'price', discount_amount: 'discount' };
            delete updated[itemId][fieldMap[field] as 'quantity' | 'price' | 'discount'];
            if (Object.keys(updated[itemId]).length === 0) {
              delete updated[itemId];
            }
          }
          return updated;
        });
      } catch (err: any) {
        console.error(`Failed to update ${field}:`, err);
      }
    }, 800); // 800ms debounce delay
  };

  const handleQuantityChange = (lineItem: OrderLineItem, value: string) => {
    // Update local editing state immediately
    setEditingValues(prev => ({
      ...prev,
      [lineItem.id]: { ...prev[lineItem.id], quantity: value }
    }));

    // Parse and debounce the API update
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      debouncedUpdate(lineItem.id, 'quantity', numValue, lineItem);
    }
  };

  const handlePriceChange = (lineItem: OrderLineItem, value: string) => {
    // Update local editing state immediately
    setEditingValues(prev => ({
      ...prev,
      [lineItem.id]: { ...prev[lineItem.id], price: value }
    }));

    // Parse and debounce the API update
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      debouncedUpdate(lineItem.id, 'unit_price', numValue, lineItem);
    }
  };

  const handleDiscountChange = (lineItem: OrderLineItem, value: string) => {
    // Update local editing state immediately
    setEditingValues(prev => ({
      ...prev,
      [lineItem.id]: { ...prev[lineItem.id], discount: value }
    }));

    // Parse and debounce the API update
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      debouncedUpdate(lineItem.id, 'discount_amount', numValue, lineItem);
    }
  };

  const handleRemoveItem = (lineItem: OrderLineItem) => {
    setItemToDelete(lineItem);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!encounter || !itemToDelete) return;

    try {
      await deleteOrderLineItem(encounter.id, itemToDelete.id);
      await fetchOrders();
      onUpdate();
    } catch (err: any) {
      console.error('Failed to remove item:', err);
    }
  };

  const filterItems = (items: CatalogItem[], searchTerm: string) => {
    if (!searchTerm.trim()) return items;
    const term = searchTerm.toLowerCase();
    return items.filter(item =>
      item.name.toLowerCase().includes(term) ||
      (item.code && item.code.toLowerCase().includes(term))
    );
  };

  const getShareTarget = (category: string, focusName?: string): { link: string; label: string } | null => {
    // Share targets always point at dashboard pages, not encounter pages.
    const normalized = (category || '').toLowerCase();
    if (normalized.includes('immun')) {
      const focus = focusName ? `?focus=${encodeURIComponent(focusName)}` : '';
      return { link: `/dashboard/pets/${petId}/immunizations${focus}`, label: 'Immunizations' };
    }
    // Labs sometimes show up as "Labs" or "Diagnostic" depending on catalog/category mapping.
    if (normalized.includes('lab') || normalized.includes('diagnostic')) {
      const focus = focusName ? `?focus=${encodeURIComponent(focusName)}` : '';
      return { link: `/dashboard/pets/${petId}/labs${focus}`, label: 'Labs' };
    }
    if (normalized.includes('med')) {
      const focus = focusName ? `?focus=${encodeURIComponent(focusName)}` : '';
      return { link: `/dashboard/pets/${petId}/medications${focus}`, label: 'Medications' };
    }
    return null;
  };

  const openShareForLineItem = (item: OrderLineItem) => {
    const target = getShareTarget(item.category, item.item_name_snapshot);
    if (!target) return;

    const cardData: CardData = {
      type: 'task',
      title: item.item_name_snapshot,
      subtitle: `${petName} • ${target.label}`,
      image_url: petPhotoUrl || undefined,
      link: target.link,
      tags: ['Tech task', target.label],
      metadata: {
        pet_id: petId,
        encounter_id: encounter?.id || '',
        order_line_item_id: item.id,
        category: item.category,
      },
    };

    composeShare(cardData);
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold mb-4">Orders & Billing</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT COLUMN: Add Items */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-700">Add Items</h3>

          {categories.map((category, idx) => (
            <div key={category.category} className="bg-white rounded-lg border border-gray-200 shadow-sm">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(idx)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
              >
                <span className="font-medium text-gray-900">{category.label}</span>
                <div className="flex items-center gap-2">
                  {category.loading && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  )}
                  {category.expanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </div>
              </button>

              {/* Category Content */}
              {category.expanded && (
                <div className="border-t border-gray-200 p-4">
                  {/* Search Box */}
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder={`Search ${category.label.toLowerCase()}...`}
                      value={searchTerms[category.category]}
                      onChange={(e) => setSearchTerms(prev => ({ ...prev, [category.category]: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Items List */}
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {filterItems(category.items, searchTerms[category.category]).map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-md group"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900 truncate">{item.name}</div>
                          <div className="text-xs text-gray-500">
                            ${item.unit_price.toFixed(2)} {item.measure_unit && `/ ${item.measure_unit}`}
                          </div>
                        </div>
                        <button
                          onClick={() => handleAddItem(item)}
                          className="ml-2 p-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Add to order"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    {filterItems(category.items, searchTerms[category.category]).length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">
                        {searchTerms[category.category] ? 'No items match your search' : 'No items available'}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* RIGHT COLUMN: Invoice */}
        <div>
          <h3 className="text-lg font-medium text-gray-700 mb-4">Invoice</h3>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
            {orderItems.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <DollarSign className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No items added yet</p>
                <p className="text-sm mt-1">Select items from the left to add them to the order</p>
              </div>
            ) : (
              <>
                {/* Line Items */}
                <div className="divide-y divide-gray-200">
                  {orderItems.map((item) => (
                    <div key={item.id} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900">{item.item_name_snapshot}</div>
                          <div className="text-xs text-gray-500">{item.category}</div>
                        </div>
                        <div className="ml-2 flex items-center gap-1">
                          {getShareTarget(item.category) && (
                            <button
                              onClick={() => openShareForLineItem(item)}
                              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-dark-bg-hover text-blue-600 dark:text-blue-400"
                              title="Share as a tech task"
                            >
                              <Share className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleRemoveItem(item)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                            title="Remove item"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-sm">
                        {/* Quantity */}
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Qty</label>
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={
                              editingValues[item.id]?.quantity !== undefined
                                ? editingValues[item.id].quantity
                                : item.quantity
                            }
                            onChange={(e) => handleQuantityChange(item, e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>

                        {/* Unit Price */}
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Price</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={
                              editingValues[item.id]?.price !== undefined
                                ? editingValues[item.id].price
                                : item.unit_price_snapshot
                            }
                            onChange={(e) => handlePriceChange(item, e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>

                        {/* Discount */}
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Disc</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={
                              editingValues[item.id]?.discount !== undefined
                                ? editingValues[item.id].discount
                                : item.discount_amount
                            }
                            onChange={(e) => handleDiscountChange(item, e.target.value)}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </div>
                      </div>

                      {/* Total */}
                      <div className="mt-2 text-right font-semibold text-gray-900">
                        ${item.total_price.toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totals Summary */}
                <div className="border-t border-gray-300 bg-gray-50 p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium">${totals.subtotal.toFixed(2)}</span>
                  </div>
                  {totals.total_discount > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Total Discount</span>
                      <span className="font-medium text-red-600">-${totals.total_discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold border-t border-gray-300 pt-2">
                    <span>Grand Total</span>
                    <span className="text-blue-600">${totals.grand_total.toFixed(2)}</span>
                  </div>
                  <div className="text-xs text-gray-500 text-right">
                    {totals.item_count} {totals.item_count === 1 ? 'item' : 'items'}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setItemToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Remove from Order?"
        message={`Are you sure you want to remove "${itemToDelete?.item_name_snapshot}" from this order?`}
        confirmText="Remove"
        cancelText="Cancel"
        isDangerous={true}
      />

      {/* Unscheduled Vaccine Warning Modal */}
      <ConfirmDialog
        isOpen={showUnscheduledConfirm}
        onClose={() => {
          setShowUnscheduledConfirm(false);
          setUnscheduledItem(null);
        }}
        onConfirm={confirmUnscheduledAdd}
        title="Unscheduled Vaccine Warning"
        message={unscheduledWarningMsg}
        confirmText="Add Anyway"
        cancelText="Cancel"
        isDangerous={false}
      />

    </div>
  );
};

export default OrdersSection;
